import { Progress } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { useCart } from '@/hooks/use-cart'
import { useCredit } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'

interface CreditFooterProps {
  onContinue: () => void
}

export const CreditFooter = ({ onContinue }: CreditFooterProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const theme = useTheme()

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const currency = useAppSelector(
    (store) => store.returnsApi.order.response.order?.money_format?.currency
  )

  const { totalAvailableCredit, spentCredit } = useCredit()

  const { cartItems, getCartTotal } = useCart()

  const cartTotalQuantity = getCartTotal(cartItems)

  const remainingCredit = totalAvailableCredit - spentCredit
  const creditPercentage = (spentCredit * 100) / totalAvailableCredit

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (isHoff) {
    return cartTotalQuantity > 0 ? (
      <div className="flex w-fit items-center justify-center rounded-t-lg bg-white p-3">
        <button
          onClick={onContinue}
          id="fc-continue-button"
          className="flex items-center justify-center gap-3 rounded-lg bg-neutral-900 px-6 py-4 text-base uppercase text-white"
        >
          {t('full_catalog.confirm')}
          <span className="flex size-6 items-center justify-center rounded-full border border-solid border-white/50 text-center text-xs font-semibold">
            {cartTotalQuantity}
          </span>
        </button>
      </div>
    ) : null
  }

  if (isMisako) {
    return cartTotalQuantity > 0 ? (
      <div className="block pb-4">
        <button
          onClick={onContinue}
          id="fc-continue-button"
          className="flex items-center justify-center gap-3 rounded-sm bg-neutral-900 px-8 py-4 text-base uppercase text-white shadow-md"
        >
          {t('full_catalog.confirm')}
          <span className="flex size-6 items-center justify-center rounded-full border border-solid border-white/50 text-center text-xs font-semibold">
            {cartTotalQuantity}
          </span>
        </button>
      </div>
    ) : null
  }

  return (
    <div className="flex w-fit flex-wrap items-center justify-center gap-x-8 gap-y-4 rounded-t-2xl border-x border-t border-solid border-neutral-200 bg-white px-6 py-4 shadow-lg">
      {remainingCredit <= 0 ? null : (
        <div className="space-y-2.5">
          <p className="flex items-center gap-2">
            <span className="rounded-full bg-radial-gradient from-brand-600 to-brand-500 px-2 py-0.5 font-medium text-white">
              {remainingCredit.toLocaleString(language, {
                style: 'currency',
                currency
              })}
            </span>
            <span className="text-sm font-light text-neutral-600">
              {t('full_catalog.to_spend_from_your_return')}
            </span>
          </p>
          <Progress
            aria-label="Spent credit"
            value={creditPercentage}
            classNames={{
              base: 'h-2',
              track: 'bg-brand-200',
              indicator: 'bg-brand-500'
            }}
          />
        </div>
      )}
      <div className="block">
        <Button
          onPress={onContinue}
          id="fc-continue-button"
          fullWidth
          variant="solid"
          radius="sm"
          style={{
            backgroundColor: theme.colors.primary.main,
            color: theme.colors.primary.contrastText,
            borderRadius: theme.borderRadius
          }}
        >
          <span className="mx-6 flex items-center justify-center gap-3 text-base">
            {t('full_catalog.continue')}
            <span className="flex size-6 items-center justify-center rounded-full border border-solid border-white/50 text-center text-xs font-semibold">
              {cartTotalQuantity}
            </span>
          </span>
        </Button>
      </div>
    </div>
  )
}
